import { useEffect, useMemo, useState } from "react";

import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { usePartialState } from "@/Common/CustomHooks/usePartialState";
import { MedfarDictionariesValidationRuleTypeEnum } from "@/Common/Models/MedfarDictionaries/Enums/MedfarDictionariesValidationRuleTypeEnum";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { PharmacyExistsParameterType } from "@/ContactDirectory/Api/ClientParameters/DirectoryPharmacyFaxNumberExistsParameters";
import { MedfarDictionariesValidationHelper } from "@/ContactDirectory/Helpers/MedfarDictionariesValidationHelper";
import { useDefaultDirectoryCountryAndState } from "@/ContactDirectory/Hooks/useDefaultDirectoryCountryAndState";
import { useMedfarDictionariesValidationRule } from "@/ContactDirectory/Hooks/useMedfarDictionariesValidationRule";
import { DirectoryAddress } from "@/ContactDirectory/Models/DirectoryAddress";
import { DirectoryFaxNumber } from "@/ContactDirectory/Models/DirectoryFaxNumber";
import { DirectoryPharmacy } from "@/ContactDirectory/Models/DirectoryPharmacy";
import { DirectoryPhoneNumber } from "@/ContactDirectory/Models/DirectoryPhoneNumber";
import { DuplicateSearchType } from "@/ContactDirectory/Models/enums/DuplicateSearchType";
import { DirectoryPharmacyValidator } from "@/ContactDirectory/Validators/DirectoryPharmacyValidator";
import { AddressFormSection } from "../Common/AddressFormSection";
import { DirectoryContactNumber } from "../Common/DirectoryContactNumber";
import { DirectoryFormRow } from "../Common/DirectoryFormRow";
import { DirectoryHeader } from "../Common/DirectoryHeader";
import { DuplicateWarningPopup } from "../DuplicatePopup/DuplicateWarningPopup";
import { IDirectoryFormProps } from "../IDirectoryFormProps";

export function PharmacyTypeForm(props: IDirectoryFormProps<DirectoryPharmacy, DirectoryPharmacy>): JSX.Element {
    const [isDuplicateFaxFromOtherPharmacy, setIsDuplicateFaxFromOtherPharmacy] = useState(false);
    const [isDuplicateAddress, setIsDuplicateAddress] = useState(false);
    const [isFaxCountryChanged, setIsFaxCountryChanged] = useState(false);
    const [isPhoneCountryChanged, setIsPhoneCountryChanged] = useState(false);

    const { defaultCountry, defaultState } = useDefaultDirectoryCountryAndState();
    const [entity, setEntityState] = usePartialState<DirectoryPharmacy>(
        () => {
            if (props.editModel)
                return props.editModel;

            return new DirectoryPharmacy({
                address: new DirectoryAddress({ countryId: defaultCountry.Id, stateId: defaultState.Id }),
                phoneNumbers: [new DirectoryPhoneNumber({ countryId: defaultCountry.Id })],
                faxNumbers: [new DirectoryFaxNumber({ countryId: defaultCountry.Id })]
            });
        },
        s => new DirectoryPharmacy(s)
    );

    function setEntity(updatedFields: Partial<DirectoryPharmacy>): void {
        const newState = setEntityState(updatedFields);
        props.onModelUpdate(newState);
    }

    function setPhoneNumber(phoneNumber: Partial<DirectoryPhoneNumber>, index: number): void {
        const phoneNumbers = [...entity.phoneNumbers];
        phoneNumbers[index] = new DirectoryPhoneNumber({ ...phoneNumbers[index], ...phoneNumber });
        setEntity({ phoneNumbers: phoneNumbers });
    }

    function setFaxNumber(faxNumber: Partial<DirectoryFaxNumber>, index: number): void {
        const faxes = [...entity.faxNumbers];
        faxes[index] = new DirectoryFaxNumber({ ...faxes[index], ...faxNumber });
        setEntity({ faxNumbers: faxes });
    }

    const setAddress = (address: Partial<DirectoryAddress>): DirectoryAddress => {
        const newAddress = new DirectoryAddress({ ...entity.address, ...address });

        const faxNumbers = isFaxCountryChanged || validationResult.faxNumber
            ? entity.faxNumbers
            : entity.faxNumbers.map(p => new DirectoryFaxNumber({ ...p, countryId: newAddress.countryId }));

        const phoneNumbers = isPhoneCountryChanged || validationResult.phoneNumber
            ? entity.phoneNumbers
            : entity.phoneNumbers.map(p => new DirectoryPhoneNumber({ ...p, countryId: newAddress.countryId }));

        setEntity({
            faxNumbers: faxNumbers,
            phoneNumbers: phoneNumbers,
            address: newAddress
        });

        return newAddress;
    };

    const validationRuleZipCode = useMedfarDictionariesValidationRule(MedfarDictionariesValidationRuleTypeEnum.PostalCode, entity.address.countryId, entity.address.stateId);
    const phoneNumberValidationRule = useMedfarDictionariesValidationRule(MedfarDictionariesValidationRuleTypeEnum.PhoneNumber, entity.address.countryId, entity.address.stateId);

    const countries = useMemo(
        () => cacheStore.GetGeographicCountriesByIsoCodes(["CA", "US"]),
        []
    );

    const countryOptions = useMemo(
        () => countries.map(p => new KeyValuePair(p.Id, p.Name)),
        [countries]
    );

    const validator = useMemo(
        () => new DirectoryPharmacyValidator(
            new MedfarDictionariesValidationHelper(cacheStore.MedfarDictionariesValidationRules),
            cacheStore.CountryCache
        ),
        []
    );

    const validationResult = useMemo(
        () => validator.validate(entity),
        [validator, entity]
    );

    const canCheckForAddressDuplicate =
        !IsNullOrWhiteSpace(entity.address?.countryId)
        && !IsNullOrWhiteSpace(entity.address?.stateId)
        && !IsNullOrWhiteSpace(entity.address?.city)
        && !IsNullOrWhiteSpace(entity.address?.streetAddress);

    useEffect(
        () => props.onValidityChange(
            validationResult.isFormValid
            && !isDuplicateFaxFromOtherPharmacy
            && !isDuplicateAddress
        ),
        [validationResult.isFormValid, isDuplicateFaxFromOtherPharmacy, isDuplicateAddress]
    );

    useEffect(
        () => {
            setIsDuplicateFaxFromOtherPharmacy(false);

            if (!validationResult.faxNumber)
                return;

            const timeout = setTimeout(
                async () => setIsDuplicateFaxFromOtherPharmacy(
                    await props.checkIfDisallowedDuplicate({
                        type: PharmacyExistsParameterType.FaxNumber,
                        faxNumber: entity.faxNumbers.firstOrDefault().number,
                        exceptPharmacyId: entity.id,
                        countryId: entity.faxNumbers.firstOrDefault().countryId
                    })
                ),
                500
            );

            return () => clearTimeout(timeout);
        },
        [entity.id, entity.faxNumbers.firstOrDefault().number, validationResult.faxNumber]
    );

    useEffect(
        () => {
            setIsDuplicateAddress(false);

            if (!canCheckForAddressDuplicate)
                return;

            const timeout = setTimeout(
                async () => setIsDuplicateAddress(
                    await props.checkIfDisallowedDuplicate({
                        type: PharmacyExistsParameterType.Address,
                        ...entity.address,
                        exceptPharmacyId: entity.id
                    })
                ),
                500
            );

            return () => clearTimeout(timeout);
        },
        [entity.id, entity.address.city, entity.address.countryId, entity.address.stateId, entity.address.streetAddress, canCheckForAddressDuplicate]
    );

    return (
        <div>
            {props.showWarningDuplicateMessage && (
                <DuplicateWarningPopup
                    {...props.duplicateWarningPopupProps}
                    onModify={entity => {
                        setEntity(entity);
                        props.duplicateWarningPopupProps.onModify(entity);
                        props.duplicateWarningPopupProps.onClose();
                    }}
                    onUsedInstead={entity => {
                        props.onEntityUseInstead(entity);
                        props.duplicateWarningPopupProps.onClose();
                    }}
                />
            )}
            <DirectoryFormRow
                labelKey="PharmacyBannerLabel"
                exampleLabelKey="PharmacyBannerExamplesLabel"
            >
                <TextboxControl
                    valid={validationResult.banner}
                    value={entity.banner}
                    onChange={banner => setEntity({ banner })}
                    maxLength={250}
                />
            </DirectoryFormRow>
            <DirectoryFormRow
                labelKey="PharmacistsLabel"
            >
                <TextboxControl
                    valid={validationResult.pharmacists}
                    value={entity.pharmacists}
                    onChange={pharmacists => setEntity({ pharmacists })}
                    maxLength={400}
                    onBlur={() => {
                        if (entity.pharmacists)
                            props.searchDuplicateForWarning(entity, entity.pharmacists, DuplicateSearchType.Pharmacist);
                    }}
                />
            </DirectoryFormRow>
            <AddressFormSection
                address={entity.address}
                setAddress={address => setAddress(address)}
                validationResult={validationResult.address}
                validationZipCode={validationRuleZipCode}
                isDuplicate={isDuplicateAddress}
                searchDuplicateForWarning={address => {
                    if (canCheckForAddressDuplicate)
                        props.searchDuplicateForWarning(entity, address, DuplicateSearchType.Address);
                }}
            />
            {phoneNumberValidationRule && (
                <>
                    <DirectoryHeader labelKey="FaxNumberLabel" />
                    {entity.faxNumbers.map((p, i) => (
                        <DirectoryContactNumber
                            key={p.id}
                            id={`faxNumber-${p.id}`}
                            number={p.number}
                            isFax={true}
                            onNumberChange={number => setFaxNumber({ number }, i)}
                            countryOptions={countryOptions}
                            selectedCountryId={p.countryId}
                            onSelectCountryId={countryId => {
                                setFaxNumber({ countryId }, i);
                                setIsFaxCountryChanged(true);
                            }}
                            description={p.description}
                            isValid={validationResult.faxNumber && !isDuplicateFaxFromOtherPharmacy && !validationResult.areNumbersDuplicate}
                            validationRule={phoneNumberValidationRule}
                            isDuplicateInDirectory={isDuplicateFaxFromOtherPharmacy}
                            onCheckForDuplicates={() => {
                                if (validationResult.faxNumber)
                                    props.searchDuplicateForWarning(entity, p, DuplicateSearchType.FaxNumber);
                            }}
                            isDuplicateInForm={validationResult.areNumbersDuplicate}
                        />
                    ))}
                </>
            )}
            {phoneNumberValidationRule && (
                <>
                    <DirectoryHeader labelKey="ContactDirectory_PhoneNumberLabel" />
                    {entity.phoneNumbers.map((p, i) => (
                        <DirectoryContactNumber
                            key={p.id}
                            id={`phoneNumber-${p.id}`}
                            number={p.number}
                            isFax={false}
                            onNumberChange={number => setPhoneNumber({ number }, i)}
                            countryOptions={countryOptions}
                            selectedCountryId={p.countryId}
                            onSelectCountryId={countryId => {
                                setPhoneNumber({ countryId }, i);
                                setIsPhoneCountryChanged(true);
                            }}
                            extension={p.extension}
                            isValid={validationResult.phoneNumber && !validationResult.areNumbersDuplicate}
                            isModifiedCountryId={false}
                            validationRule={phoneNumberValidationRule}
                            onExtensionChange={extension => setPhoneNumber({ extension }, i)}
                            isDuplicateInForm={validationResult.areNumbersDuplicate}
                        />
                    ))}
                </>
            )}
        </div>
    );
}
